import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styled, { keyframes } from 'styled-components/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import { ImageViewerPlaceholder } from 'src/client/components/Placeholders/Layouts/ImageViewerPlaceholder';
import useHammerEvents from './useHammerEvents';
import { FullScreenImageViewerFrame, ViewerButton, ViewerHeader, ViewerPagination } from './FullScreenImageViewerFrame';

const IDLE_TIME = 4000;

export type ViewerImage = {
  src: string;
  alt: string;
};

export type ViewerControls = {
  collapsed: boolean;
  slideshowActive: boolean;
  toggleCollapsed: () => void;
  toggleSlideshow: () => void;
};

type Props = {
  currentImage?: ViewerImage;
  previousImage?: ViewerImage;
  nextImage?: ViewerImage;
  position: number;
  total: number;
  autoSize: boolean;
  template?: string;
  slideshowSpeed?: number;
  resetKey?: string;
  navigationDisabled?: boolean;
  onBlockedNavigation?: () => void;
  onNavigate: (position: number) => void;
  onClose: () => void;
  header?: ReactNode;
  overlay?: ReactNode;
  renderFooter?: (controls: ViewerControls) => ReactNode;
};

export const FullScreenImageViewer = (props: Props) => {
  const {
    currentImage,
    previousImage,
    nextImage,
    position,
    total,
    autoSize,
    template = 'light',
    slideshowSpeed = 3000,
    resetKey,
    navigationDisabled = false,
    onBlockedNavigation,
    onNavigate,
    onClose,
    header,
    overlay,
    renderFooter,
  } = props;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [previousLoaded, setPreviousLoaded] = useState(false);
  const [nextLoaded, setNextLoaded] = useState(false);
  const [loadedImageNumber, setLoadedImageNumber] = useState(0);
  const [idle, setIdle] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowPlaying, setSlideshowPlaying] = useState(false);
  const [slideshowProgressVisible, setSlideshowProgressVisible] = useState(false);
  const [slideTimerRemaining, setSlideTimerRemaining] = useState(0);
  const [slideTimerStartTime, setSlideTimerStartTime] = useState(0);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const slideshowTimer = useRef(0);
  const idleTimer = useRef(0);
  const swipeEvent = useRef<((direction: 'left' | 'right') => void) | null>(null);

  const resetIdleTimer = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    setIdle(false);
    idleTimer.current = window.setTimeout(() => setIdle(true), IDLE_TIME);
  }, []);

  const handleUserActivity = useCallback(
    (event?: Event) => {
      if ((event?.target as HTMLElement | undefined)?.tagName?.toLowerCase() === 'img') {
        return;
      }
      resetIdleTimer();
    },
    [resetIdleTimer],
  );

  const endSlideshow = useCallback(() => {
    window.clearTimeout(slideshowTimer.current);
    setSlideshowActive(false);
    setSlideshowPlaying(false);
    setSlideshowProgressVisible(false);
  }, []);

  const navigate = useCallback(
    (direction: 'left' | 'right') => {
      if (!currentImage || (direction === 'left' && position === 1) || (direction === 'right' && position === total)) {
        return;
      }

      if (navigationDisabled) {
        onBlockedNavigation?.();
        return;
      }

      if ((direction === 'left' && !previousLoaded) || (direction === 'right' && !nextLoaded)) {
        setImageLoaded(false);
      }
      setPreviousLoaded(false);
      setNextLoaded(false);
      setSlideshowProgressVisible(false);
      onNavigate(position + (direction === 'left' ? -1 : 1));
    },
    [currentImage, navigationDisabled, nextLoaded, onBlockedNavigation, onNavigate, position, previousLoaded, total],
  );

  const slideshowTransition = useCallback(() => {
    if (!currentImage) return;

    setSlideshowProgressVisible(false);
    if (navigationDisabled) {
      endSlideshow();
      return;
    }

    // The timer can close over an old nextLoaded value; read the current one here.
    setNextLoaded((wasLoaded) => {
      if (!wasLoaded) setImageLoaded(false);
      return false;
    });
    setPreviousLoaded(false);
    onNavigate(position === total ? 1 : position + 1);
    setSlideTimerStartTime(Date.now());
  }, [currentImage, endSlideshow, navigationDisabled, onNavigate, position, total]);

  const nextSlideTimer = useCallback(() => {
    window.clearTimeout(slideshowTimer.current);
    slideshowTimer.current = window.setTimeout(slideshowTransition, slideshowSpeed);
    setSlideTimerStartTime(Date.now());
  }, [slideshowSpeed, slideshowTransition]);

  const toggleSlideshow = useCallback(() => {
    if (slideshowActive) {
      endSlideshow();
      return;
    }
    setSlideshowActive(true);
    setSlideshowPlaying(true);
    setSlideshowProgressVisible(true);
    nextSlideTimer();
  }, [endSlideshow, nextSlideTimer, slideshowActive]);

  const pauseSlideshow = useCallback(() => {
    if (!slideshowActive) return;
    const remaining = slideshowSpeed - (Date.now() - slideTimerStartTime);
    window.clearTimeout(slideshowTimer.current);
    setSlideshowPlaying(false);
    setSlideTimerRemaining(remaining);
  }, [slideTimerStartTime, slideshowActive, slideshowSpeed]);

  const resumeSlideshow = useCallback(() => {
    if (slideshowPlaying || !slideshowActive) return;
    setSlideshowPlaying(true);
    window.clearTimeout(slideshowTimer.current);
    slideshowTimer.current = window.setTimeout(slideshowTransition, slideTimerRemaining);
  }, [slideTimerRemaining, slideshowActive, slideshowPlaying, slideshowTransition]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((wasCollapsed) => {
      setIdle(!wasCollapsed);
      return !wasCollapsed;
    });
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.code) {
        case 'ArrowLeft':
          navigate('left');
          break;
        case 'ArrowRight':
          navigate('right');
          break;
        case 'Space':
          event.preventDefault();
          event.stopPropagation();
          toggleSlideshow();
          resetIdleTimer();
          break;
        case 'Escape':
          onClose();
          break;
        case 'ArrowUp':
          setCollapsed(false);
          resetIdleTimer();
          break;
        case 'ArrowDown':
          setCollapsed(true);
          setIdle(true);
          break;
        default:
          break;
      }
    },
    [navigate, onClose, resetIdleTimer, toggleSlideshow],
  );

  swipeEvent.current = navigate;
  useHammerEvents(autoSize, loadedImageNumber, resetIdleTimer, swipeEvent, imageRef, containerRef);

  useEffect(() => {
    document.documentElement.classList.add('showing-imageViewer');
    resetIdleTimer();
    return () => {
      document.documentElement.classList.remove('showing-imageViewer');
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(slideshowTimer.current);
    };
  }, [resetIdleTimer]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('mousedown', pauseSlideshow);
    window.addEventListener('mouseup', resumeSlideshow);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('mousedown', pauseSlideshow);
      window.removeEventListener('mouseup', resumeSlideshow);
    };
  }, [handleKeyDown, handleUserActivity, pauseSlideshow, resumeSlideshow]);

  useEffect(() => {
    if (!imageLoaded && imageRef.current) imageRef.current.style.visibility = 'hidden';
  }, [imageLoaded, currentImage?.src]);

  useEffect(() => {
    setImageLoaded(false);
    setPreviousLoaded(false);
    setNextLoaded(false);
    setLoadedImageNumber(0);
  }, [resetKey]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setLoadedImageNumber(position);
    if (slideshowActive) {
      nextSlideTimer();
      setSlideshowProgressVisible(true);
    }
  }, [nextSlideTimer, position, slideshowActive]);

  const controls: ViewerControls = { collapsed, slideshowActive, toggleCollapsed, toggleSlideshow };
  const viewerHeader =
    header ||
    (currentImage && (
      <ViewerHeader className={template}>
        <ViewerButton onClick={onClose}>Close</ViewerButton>
        <ViewerPagination>
          <span data-testid="viewer-position">{position} </span>/ {total}
        </ViewerPagination>
      </ViewerHeader>
    ));

  return (
    <FullScreenImageViewerFrame className={cx({ idle })}>
      {(!currentImage || !imageLoaded) && (
        <ViewerLoader data-testid="viewer-loader">
          <ImageViewerPlaceholder />
        </ViewerLoader>
      )}
      {viewerHeader}
      {currentImage && position > 1 && (
        <ViewerNavButton data-testid="viewer-previous" className={`${template} left`} onClick={() => navigate('left')}>
          <ViewerNavControl>
            <FontAwesomeIcon icon={faChevronLeft} />
          </ViewerNavControl>
        </ViewerNavButton>
      )}
      {currentImage && position < total && (
        <ViewerNavButton data-testid="viewer-next" className={`${template} right`} onClick={() => navigate('right')}>
          <ViewerNavControl>
            <FontAwesomeIcon icon={faChevronRight} />
          </ViewerNavControl>
        </ViewerNavButton>
      )}
      {overlay}
      {currentImage && slideshowProgressVisible && (
        <ViewerSlideshowProgress className={slideshowPlaying ? 'active' : 'paused'} slideshowSpeed={slideshowSpeed} />
      )}
      {currentImage && (
        <ViewerImageContainer
          ref={containerRef}
          data-testid="viewer-image-container"
          className={autoSize ? 'autosize' : ''}
        >
          <ViewerImageElement
            ref={imageRef}
            $autoSize={autoSize}
            alt={currentImage.alt}
            src={currentImage.src}
            onLoad={handleImageLoad}
            data-image="current"
            data-index={position}
          />
          {previousImage && (
            <ViewerPreloadImage
              data-testid="viewer-preload"
              alt=""
              src={previousImage.src}
              onLoad={() => setPreviousLoaded(true)}
            />
          )}
          {nextImage && (
            <ViewerPreloadImage
              data-testid="viewer-preload"
              alt=""
              src={nextImage.src}
              onLoad={() => setNextLoaded(true)}
            />
          )}
        </ViewerImageContainer>
      )}
      {renderFooter?.(controls)}
    </FullScreenImageViewerFrame>
  );
};
const ViewerLoader = styled.div`
  position: absolute;
  height: 100vh;
  margin: 0;
  z-index: 1;
  top: 0;
  width: 100vw;
  min-width: 100%;
  background: ${(props) => props.theme.primary1};

  /* ImageViewerPlaceholder owns its markup but does not accept a className. */
  .placeholder {
    background-color: ${(props) => props.theme.primary1};
    z-index: 1;
    position: fixed;
  }
`;

const ViewerNavButton = styled.div`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  z-index: 2;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &.dark {
    background: rgba(0, 0, 0, 0.3);
  }

  &.left {
    left: 0;
  }

  &.right {
    right: 0;
  }

  ${FullScreenImageViewerFrame}.idle & {
    opacity: 0;
    pointer-events: none;
  }

  @media (max-width: 767px) {
    width: 32px;
    height: 48px;
    margin: 0 16px;
  }

  @media (max-width: 1036px) {
    margin: 0 20px;
  }
`;

const ViewerNavControl = styled(ViewerButton)`
  font-size: 2.6rem;
  color: #444444;

  @media (max-width: 767px) {
    font-size: 1.4rem;
  }
`;

const ViewerImageContainer = styled.div`
  align-items: center;
  background: #000;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 0;
  margin: auto;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;

  &.autosize {
    height: 100%;
    max-height: 100vh;
    max-width: 100vw;
    width: 100%;
  }
`;

const ViewerImageElement = styled.img<{ $autoSize: boolean }>`
  cursor: move;
  display: block;

  ${(props) =>
    props.$autoSize &&
    `
    max-width: 100%;
    max-height: 100%;
  `}
`;

const ViewerPreloadImage = styled.img`
  cursor: move;
  display: none;
  width: 1px;
  height: 1px;
`;

const progressUp = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const ViewerSlideshowProgress = styled.div<{ slideshowSpeed: number }>`
  animation-duration: ${(props) => props.slideshowSpeed / 1000}s;
  animation-name: ${progressUp};
  animation-timing-function: linear;
  background: rgba(74, 144, 226, 0.5);
  height: 3px;
  position: absolute;
  top: 0;
  z-index: 2;

  &.paused {
    animation-play-state: paused;
  }
`;
